import {
  FormControl,
  FormControlProps,
  InputLabel,
  MenuItem,
  OutlinedInput,
  SelectProps,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { FC, ReactNode } from "react";
import { DateCell, DateCellProps } from "./FormField/DateCell";
import { NumericCell, NumericCellProps } from "./FormField/NumericCell";
import { CaseInfo, CustomPropertyType } from "generated/graphql";
import PSelect from "./PSelect";
import DebouncedText from "./DebouncedText";
import { CustomPropertySchema, CustomPropertyValue } from "helpers/customProperties";

interface Props {
  customProperty: CustomPropertySchema;
  value: CustomPropertyValue;
  setValue: (v: CustomPropertyValue) => void;
  disabled?: boolean;
  dateCellProps?: Partial<DateCellProps>;
  textProps?: Partial<TextFieldProps>;
  selectProps?: Partial<SelectProps>;
  numberProps?: Partial<NumericCellProps>;
  formControlProps?: Partial<FormControlProps>;
  caseInfo?: CaseInfo;
  debounce?: boolean;
}

const CustomPropertyCell: FC<Props> = ({
  customProperty,
  value,
  setValue,
  disabled,
  dateCellProps,
  textProps,
  selectProps,
  numberProps,
  formControlProps,
  caseInfo,
}) => {
  let component: ReactNode;

  switch (customProperty.dataType) {
    case CustomPropertyType.Date:
      component = (
        <DateCell
          required={false}
          value={value as Date | undefined}
          setValue={setValue}
          disabled={disabled}
          caseInfo={caseInfo}
          label={customProperty.displayName}
          {...dateCellProps}
        />
      );
      break;
    case CustomPropertyType.Number:
      component = (
        <NumericCell
          integerOnly={false}
          optional={true}
          value={value as number | undefined}
          setValue={(n) => (n !== undefined ? setValue(`${n}`) : setValue(undefined))}
          disabled={disabled}
          label={customProperty.displayName}
          {...numberProps}
        />
      );
      break;
    case CustomPropertyType.Select:
      component = (
        <FormControl required={false} {...formControlProps}>
          <InputLabel shrink={true}>{customProperty.displayName}</InputLabel>
          <PSelect
            displayEmpty
            value={value}
            disabled={disabled}
            label={customProperty.displayName}
            input={<OutlinedInput notched label={customProperty.displayName} />}
            onChange={(evt) => {
              const v = evt.target.value as string;
              if (v === "") setValue(undefined);
              else setValue(v);
            }}
            {...selectProps}>
            <MenuItem value="">None</MenuItem>
            {customProperty.selectOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </PSelect>
        </FormControl>
      );
      break;
    case CustomPropertyType.Text:
      component = (
        <DebouncedText
          value={value?.toString() || ""}
          disabled={disabled}
          label={customProperty.displayName}
          onChange={(v) => setValue(v)}
          textFieldProps={{ multiline: true, maxRows: 6, ...textProps }}
        />
      );
      break;
    case CustomPropertyType.Json:
      component = (
        <TextField
          value={value || ""}
          disabled={disabled}
          label={customProperty.displayName}
          onChange={(v) => {
            if (v.target.value === "") setValue(undefined);
            else setValue(v.target.value);
          }}
          {...textProps}
        />
      );
  }

  return <>{component}</>;
};

export default CustomPropertyCell;
